import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Plus, UserCircle2, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import type {
  CreateUserRequest,
  MailboxResponse,
  UpdateUserRequest,
  UserSummary,
} from "@/api/schemas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableBodySkeleton } from "@/components/ui/skeleton";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { roleRank, rolesBelow } from "@/lib/roles";
import { useAuth } from "@/stores/auth";

const USERS_QUERY = ["users", "admin"] as const;

function UsersAdminPage() {
  const { data: users, isLoading } = useQuery({
    queryKey: USERS_QUERY,
    queryFn: () =>
      apiFetch<UserSummary[]>("/api/users?include_inactive=true&scope=manage"),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserSummary | null>(null);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">Users</h1>
          <p className="mt-1 text-sm text-text-muted">
            Manage agents and admins in this organization.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="h-4 w-4" />
          Invite user
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Last login</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TableBodySkeleton rows={5} cells={6} />
          </Table>
        ) : !users || users.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
              <UserCircle2 className="h-4 w-4 text-text-muted" />
            </div>
            <div className="text-sm font-medium">No users yet</div>
            <div className="mt-1 text-xs text-text-muted">
              Click <span className="font-mono">Invite user</span> to add a teammate.
            </div>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Last login</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TBody>
              {users.map((u) => (
                <TR key={u.id}>
                  <TD className="font-medium">{u.name}</TD>
                  <TD className="text-text-muted">{u.email}</TD>
                  <TD>
                    <RoleBadge role={u.role} />
                  </TD>
                  <TD>
                    <StatusBadge isActive={u.is_active} />
                  </TD>
                  <TD className="text-xs text-text-muted">
                    {u.last_login_at ? (
                      formatAge(u.last_login_at)
                    ) : (
                      <span className="italic text-text-faint">Never</span>
                    )}
                  </TD>
                  <TD>
                    <RowActionMenu
                      user={u}
                      onEdit={() => setEditTarget(u)}
                      onDelete={() => setDeleteTarget(u)}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <EditDialog user={editTarget} onOpenChange={(o) => !o && setEditTarget(null)} />
      <DeleteDialog
        user={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
    </div>
  );
}

function RowActionMenu({
  user,
  onEdit,
  onDelete,
}: {
  user: UserSummary;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const currentUser = useAuth((s) => s.user);
  const isSelf = currentUser?.id === user.id;
  // Strict role hierarchy: caller can only edit/delete users strictly below
  // their own rank. Backend enforces this too — ``role_too_high`` 403.
  const callerRank = roleRank(currentUser?.role);
  const targetRank = roleRank(user.role);
  const canModify = targetRank < callerRank;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem disabled={!canModify} onSelect={onEdit}>
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          disabled={isSelf || !canModify}
          onSelect={onDelete}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ------------------ Invite ------------------

type InviteFormValues = CreateUserRequest;

function InviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const callerRole = useAuth((s) => s.user?.role);
  const inviteRoles = rolesBelow(callerRole);
  const isModerator = callerRole === "moderator";

  // Moderators must scope invites to a specific mailbox set; pull their
  // accessible mailboxes for the multi-select. Admin/superadmin can omit
  // (auto-grants will fan out via auto_grant_on_new_admin / new_mailbox).
  const { data: myAccess } = useQuery({
    queryKey: ["mailbox-access", "me"],
    queryFn: () =>
      apiFetch<{ mailbox_ids: string[]; delegated_mailbox_ids: string[]; role: string }>(
        "/api/mailbox-access/me",
      ),
    enabled: open,
  });
  const { data: mailboxes } = useQuery({
    queryKey: ["mailboxes"],
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
    enabled: open,
  });

  const accessibleMailboxIds = new Set(myAccess?.delegated_mailbox_ids ?? []);
  const mailboxOptions = (mailboxes ?? []).filter((m) =>
    accessibleMailboxIds.has(m.id),
  );

  const form = useForm<InviteFormValues>({
    defaultValues: {
      email: "",
      name: "",
      // `inviteRoles` excludes "superadmin" by construction; cast is safe.
      role: (inviteRoles[0] ?? "agent") as InviteFormValues["role"],
      password: "",
      mailbox_grants: null,
    },
  });
  const [selectedMailboxes, setSelectedMailboxes] = useState<Set<string>>(new Set());

  const mutation = useMutation({
    mutationFn: (values: InviteFormValues) =>
      apiFetch<UserSummary>("/api/users", {
        method: "POST",
        json: {
          ...values,
          mailbox_grants: selectedMailboxes.size > 0 ? Array.from(selectedMailboxes) : null,
        },
      }),
    onSuccess: (created) => {
      toast.success(`Invited ${created.email}`);
      void qc.invalidateQueries({ queryKey: USERS_QUERY });
      onOpenChange(false);
      form.reset();
      setSelectedMailboxes(new Set());
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not invite user");
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          form.reset();
          setSelectedMailboxes(new Set());
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
          <DialogDescription>
            The new user can log in with this email and temporary password, then
            change their password from their profile.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => {
            if (isModerator && selectedMailboxes.size === 0) {
              toast.error("Pick at least one mailbox to grant the new user.");
              return;
            }
            mutation.mutate({ ...v, email: v.email.trim().toLowerCase() });
          })}
          className="flex flex-col gap-3"
        >
          <FormField label="Name">
            <Input
              {...form.register("name", { required: true, minLength: 1 })}
              placeholder="Jane Doe"
              autoFocus
            />
          </FormField>
          <FormField label="Email">
            <Input
              type="email"
              {...form.register("email", { required: true })}
              placeholder="jane@company.com"
            />
          </FormField>
          <FormField label="Role">
            <Select
              value={form.watch("role")}
              onValueChange={(v) =>
                form.setValue("role", v as InviteFormValues["role"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {inviteRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {capitalise(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField
            label={isModerator ? "Mailboxes (required)" : "Mailboxes (optional)"}
            hint={
              isModerator
                ? "Moderator invites are scoped — pick at least one mailbox you manage."
                : "Optional: pre-grant the new user access to specific mailboxes. Otherwise, default org-wide access applies."
            }
          >
            <div className="flex flex-col gap-1.5">
              {mailboxOptions.length === 0 ? (
                <p className="text-xs text-text-muted italic">
                  No mailboxes available — connect one first.
                </p>
              ) : (
                mailboxOptions.map((m) => {
                  const checked = selectedMailboxes.has(m.id);
                  return (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedMailboxes((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(m.id);
                            else next.delete(m.id);
                            return next;
                          });
                        }}
                        className="h-4 w-4 rounded border-border"
                      />
                      <span>{m.display_name || m.email_address}</span>
                    </label>
                  );
                })
              )}
            </div>
          </FormField>
          <FormField label="Temporary password" hint="Minimum 8 characters">
            <Input
              type="text"
              {...form.register("password", { required: true, minLength: 8 })}
              placeholder="pick-something-memorable"
            />
          </FormField>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ------------------ Edit ------------------

type EditFormValues = {
  name: string;
  role: "admin" | "moderator" | "agent";
  is_active: boolean;
};

function EditDialog({
  user,
  onOpenChange,
}: {
  user: UserSummary | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const currentUser = useAuth((s) => s.user);
  const editableRoles = rolesBelow(currentUser?.role);
  const form = useForm<EditFormValues>({
    values: user
      ? {
          name: user.name,
          role: (user.role === "superadmin" ? "admin" : user.role) as
            | "admin"
            | "moderator"
            | "agent",
          is_active: user.is_active,
        }
      : undefined,
  });

  const mutation = useMutation({
    mutationFn: (body: UpdateUserRequest) => {
      if (!user) throw new Error("no user");
      return apiFetch<UserSummary>(`/api/users/${user.id}`, {
        method: "PATCH",
        json: body,
      });
    },
    onSuccess: (updated) => {
      toast.success(`Updated ${updated.email}`);
      void qc.invalidateQueries({ queryKey: USERS_QUERY });
      // Also refresh the org-member list used by the assignee combobox.
      void qc.invalidateQueries({ queryKey: ["users", "org"] });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not update user");
    },
  });

  if (!user) return null;

  const isSelf = currentUser?.id === user.id;

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => {
            const body: UpdateUserRequest = {};
            if (v.name !== user.name) body.name = v.name.trim();
            if (v.role !== user.role) body.role = v.role;
            if (v.is_active !== user.is_active) body.is_active = v.is_active;
            if (Object.keys(body).length === 0) {
              onOpenChange(false);
              return;
            }
            mutation.mutate(body);
          })}
          className="flex flex-col gap-3"
        >
          <FormField label="Name">
            <Input
              {...form.register("name", { required: true, minLength: 1 })}
              autoFocus
            />
          </FormField>
          <FormField label="Role">
            <Select
              value={form.watch("role")}
              onValueChange={(v) =>
                form.setValue("role", v as EditFormValues["role"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {editableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {capitalise(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField
            label="Active"
            hint={
              isSelf
                ? "You cannot deactivate your own account."
                : "Deactivated users cannot log in."
            }
          >
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...form.register("is_active")}
                disabled={isSelf}
                className="h-4 w-4 rounded border-border"
              />
              <span>Account is active</span>
            </label>
          </FormField>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Delete ------------------

function DeleteDialog({
  user,
  onOpenChange,
}: {
  user: UserSummary | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("no user");
      return apiFetch<void>(`/api/users/${user.id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      toast.success(`Deleted ${user?.email ?? "user"}`);
      void qc.invalidateQueries({ queryKey: USERS_QUERY });
      void qc.invalidateQueries({ queryKey: ["users", "org"] });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delete user");
    },
  });

  if (!user) return null;

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete user</DialogTitle>
          <DialogDescription>
            Delete <span className="font-medium text-text">{user.email}</span>?
            They will no longer be able to log in. This is a soft delete and can
            be reversed by restoring from the database.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Small helpers ------------------

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === "superadmin"
      ? "bg-danger-subtle text-danger"
      : role === "admin"
        ? "bg-accent-subtle text-accent"
        : role === "moderator"
          ? "bg-warning-subtle text-warning"
          : "bg-surface-subtle text-text-muted";
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-mono ${cls}`}>{role}</span>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="rounded bg-success-subtle px-1.5 py-0.5 text-xs text-success">
      active
    </span>
  ) : (
    <span className="rounded bg-surface-subtle px-1.5 py-0.5 text-xs text-text-muted">
      inactive
    </span>
  );
}

function formatAge(iso: string): string {
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${Math.floor(diffHour / 24)}d ago`;
}

export const Route = createFileRoute("/_auth/settings/users")({
  component: UsersAdminPage,
});
